var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { stateKey } from './fieldDefinitionTypes';
/**
 * Separator for a synthetic id.
 *
 * It has to be inert in three places at once, which rules out most candidates:
 * a react-hook-form path (splits on `.`, `[`, `]`), a DOM `id`
 * (`DynamicFieldPanel` builds `sf-{pathPrefix}-{code}` from it), and a CSS `#id`
 * selector — the addressing convention CZNA-684 established and the e2e suite
 * uses (`page.locator('#sf-fields-670-0-a')`).
 *
 * `#` was the first choice, because `${tag}#${field_index}` is already this
 * file's neighbour's convention for "same tag, nth occurrence". But that one is
 * only ever a `Map` key; it never reaches a DOM id, and `#` inside one makes the
 * element unaddressable by the selector everyone here reaches for. `__` has the
 * same properties and none of that hazard, and it cannot occur in a MARC tag.
 */
var SYNTHETIC_SEPARATOR = '__';
/**
 * Rewrites the config so that no two FieldDefs share a `stateKey`.
 *
 * Returns the SAME object when there is nothing to disambiguate — the common
 * case, and it keeps the referential identity react-query hands out stable, so
 * a normalized config cannot become a new render trigger.
 *
 * When a duplicate is found the config is rebuilt structurally (new tab and
 * field objects for the affected entries only); the input is never mutated,
 * because it is react-query's cached payload and is shared with other readers.
 */
export var disambiguateFieldIds = function (config) {
    // Every identity the config DECLARES, collected before a single synthetic is
    // handed out. Order matters here: assigning as we walk would let a synthetic
    // `370__2` take the name of an explicitly authored `370__2` further down the
    // config, and then rename the author's field to `370__3`. An id somebody chose
    // outranks one we invented, wherever in the file it sits.
    var declared = new Set();
    for (var _i = 0, _a = config.tabs || []; _i < _a.length; _i++) {
        var tab = _a[_i];
        for (var _b = 0, _c = tab.fields || []; _b < _c.length; _b++) {
            var field = _c[_b];
            declared.add(stateKey(field));
        }
    }
    var seen = new Set();
    var changed = false;
    var tabs = (config.tabs || []).map(function (tab) {
        var tabChanged = false;
        var fields = (tab.fields || []).map(function (field) {
            var identity = stateKey(field);
            if (!seen.has(identity)) {
                seen.add(identity);
                return field;
            }
            // Second and further occurrence: find the lowest ordinal that is free
            // against both what has been handed out AND what the config declares
            // anywhere. The loop matters — a config may already contain a literal
            // `370__2`, and handing out a key that is taken would reproduce the very
            // collision this exists to remove.
            var ordinal = 2;
            var synthetic = "".concat(identity).concat(SYNTHETIC_SEPARATOR).concat(ordinal);
            while (seen.has(synthetic) || declared.has(synthetic)) {
                ordinal += 1;
                synthetic = "".concat(identity).concat(SYNTHETIC_SEPARATOR).concat(ordinal);
            }
            seen.add(synthetic);
            changed = true;
            tabChanged = true;
            return __assign(__assign({}, field), { fieldId: synthetic });
        });
        return tabChanged ? __assign(__assign({}, tab), { fields: fields }) : tab;
    });
    if (!changed)
        return config;
    // A collapsed field is invisible from inside the form — it looks like a field
    // that was simply configured twice — so say it once, loudly, where whoever
    // edits the template can see it.
    // eslint-disable-next-line no-console
    if (typeof console !== 'undefined' && console.warn) {
        // eslint-disable-next-line no-console
        console.warn('[disambiguateFieldIds] the form config declares several fields with the same '
            + 'identity (fieldId, else tag); the later ones were given a synthetic fieldId so '
            + 'they get their own form state. Give them explicit fieldIds in the template '
            + '(as 024-isni / 024-wikidata do) — see CZNA-715.');
    }
    return __assign(__assign({}, config), { tabs: tabs });
};
/**
 * The identities a config declares more than once, in config order.
 * Exported for tests and for anything that wants to report the problem rather
 * than repair it.
 */
export var duplicateFieldIdentities = function (config) {
    var seen = new Set();
    var dups = [];
    for (var _i = 0, _a = config.tabs || []; _i < _a.length; _i++) {
        var tab = _a[_i];
        for (var _b = 0, _c = tab.fields || []; _b < _c.length; _b++) {
            var field = _c[_b];
            var identity = stateKey(field);
            if (seen.has(identity)) {
                if (dups.indexOf(identity) === -1)
                    dups.push(identity);
            }
            else {
                seen.add(identity);
            }
        }
    }
    return dups;
};
